import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { addTruckPopup, trucksSearchFAQ } from 'constants/pages';
import { tabIds } from 'constants/ui';
import { useStore } from 'hooks/useStore';
import { TabsContent } from 'layout/TabsContent';
import { QueryType } from 'store/QueryStore/QueryStore';
import { SavedFilter } from 'store/TabsDataStore/TabsDataStore';
import { openPopupOnCenter } from 'utils/nativeDOM';
import { interpolate } from 'utils/string/format';

import { TrucksIcon } from './TrucksIcon';

import css from './Trucks.module.css';

const Trucks = (props: { className?: string }) => {
  const {
    siteFeatures,
    app: { i18n, env, fillFilterFromQuery, loading },
    query,
    tabsData,
    ui,
  } = useStore();

  const { trucks } = tabsData;

  useEffect(() => {
    const onTruckAdded = (event: MessageEvent) => {
      if (event.data) {
        try {
          const parsedData = JSON.parse(event.data);

          if (parsedData.eventType === 'saveFilterEvent') {
            trucks.fetchTrucksData({ isForced: true });
          }
        } catch (error) {}
      }
    };

    window.addEventListener('message', onTruckAdded, false);

    return () => {
      window.removeEventListener('message', onTruckAdded, false);
    };
  }, [trucks, tabsData, siteFeatures.isSiteFeatureEnabled]);

  const onAddTruck = () => {
    openPopupOnCenter({ url: interpolate(addTruckPopup, { env }), h: 520, w: 600 });
  };

  const handleFilterClick = async (filter: SavedFilter) => {
    ui.setActiveTab(tabIds.search);
    trucks.selectFilter(filter);
    query.setParsedQueryData(filter.filter);
    query.setType(QueryType.byFilter);
    await fillFilterFromQuery({ skipQueryParsing: true });
  };

  const handlePageChange = () => {
    trucks.fetchTrucksData();
  };

  const truckSearches = trucks.truckSearches;

  const totalItems = trucks.totalItems;

  useEffect(() => {
    if (trucks.truckSearches.length > 0) return;

    trucks.fetchTrucksData();
  }, [trucks]);

  return (
    <TabsContent<SavedFilter>
      activeItem={trucks.selectedFilter}
      className={props.className}
      header={
        <TabsContent.Header>
          <div className={css.linksContainer}>
            <button className={cn('glz-link', css.addTruck)} onClick={onAddTruck}>
              {i18n.trucks.addTruck}
            </button>

            <a
              className={cn('glz-link', 'glz-is-gray', css.link)}
              href={interpolate(trucksSearchFAQ, { env })}
              rel="noreferrer"
              target="_blank"
            >
              {i18n.trucks.moreOnTrucks}
            </a>
          </div>
        </TabsContent.Header>
      }
      isLoading={loading.tabsData}
      items={truckSearches || []}
      itemsCounter="truck"
      noContentElement={
        <TabsContent.NoContent
          actionElement={
            <button
              className={cn(css.button, 'glz-button', 'glz-is-primary', 'glz-is-small')}
              type="button"
              onClick={onAddTruck}
            >
              {i18n.trucks.addTruck}
            </button>
          }
          desc={i18n.trucks.noContent.desc}
          icon={<TrucksIcon />}
          title={i18n.trucks.noContent.title}
        />
      }
      totalItems={totalItems || 0}
      onItemClick={handleFilterClick}
      onPageChange={handlePageChange}
    />
  );
};

const connected = observer(Trucks);

export { connected as Trucks };
